import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { KontrollterminGenerationService } from '../../../services/kontrolltermin-generation.service';
import { MitarbeiterService } from '../../../services/mitarbeiter.service';
import { environment } from '../../../../environments/environment';

export interface EditKontrollterminData {
  kontrolltermin: any;
  standortId: string;
}

@Component({
  selector: 'app-edit-kontrolltermin-dialog',
  templateUrl: './edit-kontrolltermin-dialog.component.html',
  styleUrls: ['./edit-kontrolltermin-dialog.component.scss']
})
export class EditKontrollterminDialogComponent implements OnInit {
  isLoading = false;
  isSaving = false;
  
  // Available options
  technikerOptions: any[] = [];
  statusOptions: any[] = [];
  
  // ngModel properties
  selectedTechnikerId: string = '';
  selectedStatusId: string = '';
  
  // Environment for template access
  environment = environment;

  constructor(
    private dialogRef: MatDialogRef<EditKontrollterminDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditKontrollterminData,
    private kontrollterminService: KontrollterminGenerationService,
    private mitarbeiterService: MitarbeiterService,
    private snackBar: MatSnackBar
  ) {
  }

  ngOnInit(): void {
    this.loadOptions();
  }

  private loadOptions(): void {
    console.log('=== LOAD OPTIONS CALLED ===');
    this.isLoading = true;
    
    // Load status options from environment first
    this.statusOptions = environment.kontrolltermin_status || [];
    
    // Load techniker options
    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        this.technikerOptions = response.data || [];
        console.log('Loaded techniker options:', this.technikerOptions);
        console.log('First techniker option structure:', this.technikerOptions[0]);
        this.isLoading = false;
        // Populate form after techniker options are loaded
        this.populateForm();
      },
      error: (error) => {
        console.error('Error loading techniker:', error);
        this.snackBar.open('Fehler beim Laden der Techniker', 'Schließen', { duration: 3000 });
        this.isLoading = false;
        // Still populate form even if techniker loading fails
        this.populateForm();
      }
    });

    // Fallback: populate form after a timeout to ensure it's called even if API fails
    setTimeout(() => {
      if (this.isLoading) {
        console.log('Fallback: populating form after timeout');
        this.isLoading = false;
        this.populateForm();
      }
    }, 2000);
  }

  private populateForm(): void {
    const kt = this.data.kontrolltermin;
    
    console.log('=== DEBUG POPULATE FORM ===');
    console.log('Kontrolltermin data:', kt);
    console.log('kt.techniker_id:', kt.techniker_id, typeof kt.techniker_id);
    console.log('kt.status_id:', kt.status_id, typeof kt.status_id);
    console.log('Available techniker options:', this.technikerOptions);
    console.log('Available status options:', this.statusOptions);
    
    // Set ngModel values directly
    let formTechnikerId = kt.techniker_id || '';
    let foundTechniker: any = null;
    
    // Handle data type mismatch: kontrolltermin has string ID, techniker options have numeric ID
    if (formTechnikerId && this.technikerOptions.length > 0) {
      // Convert string ID to number for comparison
      const numericId = parseInt(formTechnikerId, 10);
      
      console.log('Looking for techniker with ID:', formTechnikerId, 'converted to:', numericId);
      console.log('Available techniker IDs:', this.technikerOptions.map(t => ({ id: t.id, type: typeof t.id, display_name: t.display_name })));
      
      foundTechniker = this.technikerOptions.find(t => 
        t.id === numericId || 
        t.id === formTechnikerId ||
        t.id === formTechnikerId.toString()
      );
      
      if (foundTechniker) {
        formTechnikerId = foundTechniker.id.toString();
        console.log('Found matching techniker for form:', foundTechniker);
      } else {
        console.log('No matching techniker found for ID:', formTechnikerId);
        console.log('Available techniker IDs:', this.technikerOptions.map(t => t.id));
      }
    }
    
    // Set ngModel values - use the actual techniker ID (number) for mat-select
    this.selectedTechnikerId = foundTechniker ? foundTechniker.id : formTechnikerId;
    
    // Handle status ID - check if it matches any status option
    let statusId = kt.status_id || '';
    console.log('Original status_id from kontrolltermin:', statusId, typeof statusId);
    console.log('Available status options:', this.statusOptions);
    
    // Find matching status option
    const foundStatus = this.statusOptions.find(s => 
      s.id === statusId || 
      s.id === parseInt(statusId, 10) ||
      s.id === statusId.toString()
    );
    
    if (foundStatus) {
      this.selectedStatusId = foundStatus.id;
      console.log('Found matching status:', foundStatus);
    } else {
      this.selectedStatusId = statusId;
      console.log('No matching status found for ID:', statusId);
      console.log('Available status IDs:', this.statusOptions.map(s => s.id));
    }
    
    console.log('Set selectedTechnikerId:', this.selectedTechnikerId, typeof this.selectedTechnikerId);
    console.log('Set selectedStatusId:', this.selectedStatusId, typeof this.selectedStatusId);
    console.log('=== END DEBUG ===');
  }

  onSave(): void {
    // Validate ngModel values
    if (!this.selectedTechnikerId || !this.selectedStatusId) {
      this.snackBar.open('Bitte füllen Sie alle Felder aus', 'Schließen', { duration: 3000 });
      return;
    }
    
    this.isSaving = true;
    const formData = {
      techniker_id: this.selectedTechnikerId,
      status_id: this.selectedStatusId
    };
      
    console.log('Updating kontrolltermin:', this.data.kontrolltermin.id, 'with data:', formData);
    
    // Call the API to update the kontrolltermin
    this.kontrollterminService.updateKontrolltermin(this.data.kontrolltermin.id, formData).subscribe({
      next: (response) => {
        console.log('Update response:', response);
        // The API returns the updated kontrolltermin object directly, not wrapped in success/data
        if (response && response.id) {
          // Find the updated techniker and status names
          const updatedTechniker = this.technikerOptions.find(t => t.id == this.selectedTechnikerId);
          const updatedStatus = this.statusOptions.find(s => s.id == this.selectedStatusId);
          
          const updatedKontrolltermin = {
            ...this.data.kontrolltermin,
            techniker_id: this.selectedTechnikerId,
            status_id: this.selectedStatusId,
            techniker_name: updatedTechniker ? updatedTechniker.display_name : this.data.kontrolltermin.techniker_name,
            status_name: updatedStatus ? updatedStatus.label : this.data.kontrolltermin.status_name,
            status_badge_class: this.getStatusBadgeClass(this.selectedStatusId)
          };
          
          console.log('Returning updated kontrolltermin:', updatedKontrolltermin);
          
          this.snackBar.open('Kontrolltermin erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
          this.dialogRef.close(updatedKontrolltermin);
        } else {
          this.snackBar.open('Fehler beim Aktualisieren des Kontrolltermins', 'Schließen', { duration: 5000 });
        }
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error updating kontrolltermin:', error);
        this.snackBar.open('Fehler beim Aktualisieren des Kontrolltermins', 'Schließen', { duration: 5000 });
        this.isSaving = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getTechnikerName(technikerId: string): string {
    console.log('Looking for techniker with ID:', technikerId);
    console.log('Available techniker options:', this.technikerOptions);
    
    // Handle data type mismatch: technikerId is string, but techniker options have numeric IDs
    const numericId = parseInt(technikerId, 10);
    
    const techniker = this.technikerOptions.find(t => 
      t.id === numericId || 
      t.id === technikerId ||
      t.id === technikerId.toString()
    );
    
    console.log('Found techniker:', techniker);
    
    return techniker ? techniker.display_name : 'Unbekannt';
  }

  getStatusLabel(statusId: number | string): string {
    const id = typeof statusId === 'string' ? parseInt(statusId, 10) : statusId;
    const status = this.statusOptions.find(s => s.id === id);
    return status ? status.label : 'Unbekannt';
  }

  getStatusBadgeClass(statusId: number | string): string {
    const id = typeof statusId === 'string' ? parseInt(statusId, 10) : statusId;
    const classMap: {[key: number]: string} = {
      1: 'bg-red-200',      // geloescht
      2: 'bg-orange-200',   // abgebrochen
      3: 'bg-red-200',      // fehlerhaft
      4: 'bg-blue-200',     // geplant
      5: 'bg-yellow-200',   // aktuell
      6: 'bg-yellow-200',   // in_bearbeitung
      7: 'bg-purple-200',   // freigabe
      8: 'bg-green-200'     // abgeschlossen
    };
    return classMap[id] || 'bg-gray-200';
  }
}